import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Outlet, useNavigate, useParams } from "react-router-dom";
import { toast } from 'sonner';
import TabNav, { MenuItem } from "../../../components/navigation/TabNav";
import useFetch from '../../../hooks/useFetch';
import useHandleErrors from '../../../hooks/useHandleErrors';
import PageHeader from "../../../layout/shared/page-header";
import { RootState } from '../../../redux/store';
import { setSelectedView } from '../../../redux/userProfileSlice';
import { menuRoutes } from "../../../router/menu";
import { ClientService } from "../../../services/clients/clientService";
import CustomSpinner from '../../../loader/CustomSpinner';
import {ClientApiResponse} from "../../../type/entities/client-type";

const menuItems: MenuItem[] = [
    {
        label: 'Información',
        path: menuRoutes.clients.profile.info,
        permission: {
            group: 'client',
            action: 'get',
        },
    },
    {
        label: 'Proformas',
        path: menuRoutes.clients.profile.proformas,
        permission: {
            group: 'invoice',
            action: 'list',
        },
    },
    {
        label: 'Facturas',
        path: menuRoutes.clients.profile.facturas,
        permission: {
            group: 'invoice',
            action: 'list',
        },
    }
];

export default function ClientProfileLayout({ isLoading }: { isLoading?: boolean }) {

    // HOOKS

    const { selectedView } = useSelector((state: RootState) => state.clientProfile);
    const { id = '' } = useParams<{ id: string }>();
    const { handleErrors } = useHandleErrors();
    const navigate = useNavigate();
    const service = new ClientService();

    // FUNCTIONS

    //--------------------------------------------------------------------------------------------------------------
    /**
     * @ES Obtener los datos del usuario
     *
     * @EN Get the user data
     */
    //--------------------------------------------------------------------------------------------------------------
    const [data, loading, error, refetch] = useFetch(useCallback(async () => {
        if (!id || id === '') return null;
        const response = await service.getClientById(id);
        return response.getResponseData() as ClientApiResponse;
    }, [id]));
    //--------------------------------------------------------------------------------------------------------------

    const pageHeader = {
        title: `Perfil de ${data?.companyName || 'Cliente'}`,
        breadcrumb: [
            { name: 'Administración' },
            { name: 'Clientes', href: menuRoutes.clients.path },
            { name: 'Perfil de Cliente' },
        ],
    };

    //--------------------------------------------------------------------------------------------------------------
    /**
     * @ES Manejar el borrado del cliente
     *
     * @EN Handle the client deletion
     */
    //--------------------------------------------------------------------------------------------------------------
    const handleDelete = async (id: string) => {
        try {
            const response = await (await service.deleteClient(id)).getResponseData();
            if (response.success) {
                navigate(menuRoutes.clients.path);
                toast.success('Se ha eliminado correctamente');
            } else {
                handleErrors(response);
            }
        } catch (error) {
            handleErrors(error);
        }
    };
    //--------------------------------------------------------------------------------------------------------------

    // USE EFFECTS

    useEffect(() => {
        navigate(`${menuRoutes.clients.path}/${id}/profile/${selectedView}`);
    }, [selectedView]);

    // RENDER

    return (
        <>
            <PageHeader title={pageHeader.title} breadcrumb={pageHeader.breadcrumb}>
                <div className='flex flex-row flex-nowrap gap-3 mt-3 md:mt-0'>
                </div>
            </PageHeader>

            <TabNav menuItems={menuItems} setSelectedView={setSelectedView} />

            {isLoading ? <div className='m-auto h-full'><CustomSpinner /></div> : <Outlet />}
        </>
    );
}